<template>
  <div class="tooltip" ref="toolTipContainerRef" role="tooltip">
    <Transition name="fade">
      <div
        v-if="isToolTipOpen"
        class="tool-tip-floating bg-white shadow p-space-xxs rounded-sm fs-xs"
        :style="[floatingStyles, setTooltipWidth]"
        ref="toolTipRef"
        role="tooltip"
        :aria-hidden="!isToolTipOpen"
        aria-live="assertive"
        tabindex="0"
      >
        <slot name="tooltip"></slot>
      </div>
    </Transition>

    <button
      class="tool-tip-icon ms-space-xxs"
      ref="reference"
      @focus="showToolTip"
      @click="showToolTip"
      @mouseenter="showToolTip"
      @mouseleave="hideToolTip"
      @blur="hideToolTip"
      aria-label="Toggle tooltip"
    >
      <slot></slot>
    </button>
  </div>
</template>
<script lang="ts" setup>
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  type OffsetOptions,
  type Placement,
} from "@floating-ui/vue";
import { onClickOutside } from "@vueuse/core";

interface Props {
  placement?: Placement;
  offset?: OffsetOptions;
  transform?: boolean;
  tooltipWidth?: number;
}

const props = withDefaults(defineProps<Props>(), {
  placement: "right-start",
  offset: 0,
  tooltipWidth: 210,
});

const isToolTipOpen = ref(false);
const toolTipRef = ref<HTMLElement | null>(null);
const reference = ref<HTMLElement | null>(null);
const toolTipContainerRef = ref<HTMLElement | null>(null);
const { floatingStyles } = useFloating(reference, toolTipRef, {
  whileElementsMounted: autoUpdate,
  strategy: "absolute",
  open: isToolTipOpen,
  transform: props.transform,
  placement: props.placement,
  middleware: [flip(), offset(props.offset), shift()],
});

onClickOutside(toolTipContainerRef, () => {
  hideToolTip();
});

const showToolTip = () => {
  console.log("show tooltip");
  isToolTipOpen.value = true;
};

const hideToolTip = () => {
  isToolTipOpen.value = false;
};

const setTooltipWidth = computed(() => {
  return { "--tt-width": `${props.tooltipWidth}px` };
});

defineExpose<{
  hideTooltip: () => void;
}>({
  hideTooltip: hideToolTip,
});
</script>

<style lang="scss" scoped>
.tool-tip-icon {
  background-color: transparent;
  border: none;
  cursor: pointer;
  appearance: none;
  padding: 0;
  margin: 0;
}

.tool-tip-floating {
  position: absolute;
  z-index: 1000;
  padding: 0.5rem;
  max-width: 210px;
  white-space: normal;
  word-break: break-word;
  word-wrap: break-word;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
@media screen and (min-width: 992px) {
  .tool-tip-floating {
    max-width: var(--tt-width);
    width: var(--tt-width);
  }
}
</style>
